import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, tap, take } from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { validateGraphQLErrors } from '@core/utils/errors';
import { AuthService } from '@core/services/auth.service';
import { ProfileService } from '@core/services/profile.service';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectLastSMSSent } from '@state/auth/auth.selectors';
import { resetConfigAction } from '@state/config/config.actions';
import { resetPropertiesAction } from '@state/properties/properties.actions';
import { resetOffersAction } from '@state/offers/offers.actions';
import { resetMyDealsAction } from '@state/my-deals/my-deals.actions';
import { resetMyOrdersAction } from '@state/my-orders/my-orders.actions';
import { resetMessagesAction } from '@state/messages/messages.actions';
import { resetBuyBoxesAction } from '@state/buy-boxes/buy-boxes.actions';
import { resetFavoritesAction } from '@state/favorites/favorites.actions';
// import { resetFiltersAction } from '@state/filters/filters.actions';
import { setOrganizationUrlSlugAction } from '@state/organization/organization.actions';
import { selectOrganizationUrlSlug } from '@state/organization/organization.selectors';

import {
  signInAction,
  signInActionSuccess,
  signInActionFailure,
  signInWithMagicLinkAction,
  signInWithMagicLinkActionFailure,
  signInWithEmailMagicLinkAction,
  signInWithEmailMagicLinkActionFailure,
  signInWithSmsMagicLinkAction,
  signInWithSmsMagicLinkActionFailure,
  signUpAction,
  signUpActionSuccess,
  signUpActionFailure,
  registerPasswordlessAction,
  registerPasswordlessActionFailure,
  signOutAction,
  fetchUserAction,
  fetchUserActionSuccess,
  fetchUserActionFailure,
  setupAccountAction,
  setupAccountActionSuccess,
  setupAccountActionFailure,
  updateUserPreferenceAction,
  updateUserPreferenceActionSuccess,
  updateUserPreferenceActionFailure,
  requestPasswordResetAction,
  requestPasswordResetActionFinish,
  resetPasswordAction,
  resetPasswordActionFinish,
  sendPhoneNumberConfirmationAction,
  confirmPhoneNumberAction,
  setLoadingAction,
  setLastSMSSentAction,
  updatePhoneNumberAction,
  updatePhoneNumberSuccessAction,
  setReferrerCodeAction,
  setEntityIdAction,
  updateProfileAction,
  updateProfileActionSuccess,
  updateProfileActionFailure,
  updatePasswordAction,
  updatePasswordActionSuccess,
  updatePasswordActionFailure,
  setAuthModal,
  updatePhoneNumberActionFailure,
} from './auth.actions';

import { DEFAULT_FEATURE_FLAGS } from '@core/constants/feature-flags';
import { fetchFeatureFlagsAction } from '../config/config.actions';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private profileService: ProfileService,
    private router: Router,
    private toastr: ToastrService,
    private store: Store<AppState>
  ) {}

  signInUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signInAction),
      switchMap((action) =>
        this.authService.signInUser(action).pipe(
          map((res) => {
            if (res.signInWithPassword.token) {
              return signInActionSuccess(res.signInWithPassword);
            }
            throw Error('The email or password is incorrect');
          }),
          catchError(() => {
            this.toastr.error('The email or password is incorrect.');
            return [signInActionFailure({ errors: [] })];
          })
        )
      )
    )
  );

  signInUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signInActionSuccess),
        tap((userWithToken) => {
          this.store.dispatch(
            fetchFeatureFlagsAction({ keys: [...DEFAULT_FEATURE_FLAGS] })
          );

          const currentUrl = this.router.routerState.snapshot.url;

          this.toastr.clear();
          this.toastr.success('You have logged in.');

          if (
            userWithToken.emailConfirmedAt &&
            userWithToken.phoneNumberConfirmedAt
          ) {
            this.store.dispatch(setReferrerCodeAction({ referrerCode: null }));
            this.store.dispatch(setAuthModal({ authModal: undefined }));
          }

          if (!!userWithToken.preference) {
            this.store.dispatch(setEntityIdAction({ entityId: null }));

            // Get current url, if /properties/:slug or /auth/* do not redirect
            // /auth/* is skipped because magic link navigation is handled separately
            if (
              !currentUrl.includes('/properties/') &&
              !currentUrl.startsWith('/auth/')
            ) {
              /**
               * Redirect to /my-organization if preference has WHOLESALER
               * inside roles and has not setup their organization yet.
               * Redirect to /my-deals if preference has WHOLESALER
               * inside roles and has setup their organization.
               */
              if (
                userWithToken.preference.roles &&
                userWithToken.preference.roles.includes('WHOLESALER')
              ) {
                if (
                  !userWithToken.organization ||
                  !userWithToken.organization.shortName
                ) {
                  this.router.navigate(['/', 'my-organization']);
                } else if (userWithToken?.organization?.shortName) {
                  this.store.dispatch(
                    setOrganizationUrlSlugAction({
                      slug: userWithToken.organization.shortName,
                    })
                  );
                  this.router.navigate(['/', 'my-deals']);
                }
              } else {
                // If not a wholesaler and it was under an external MP,
                // redirect to /o/org-short-name
                this.store
                  .select(selectOrganizationUrlSlug)
                  .pipe(take(1))
                  .subscribe((slug) => {
                    if (slug) {
                      this.router.navigate(['/o', slug]);
                    } else {
                      this.router.navigate(['/']);
                    }
                  });
              }
            }
          }
        })
      ),
    { dispatch: false }
  );

  signInWithMagicLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signInWithMagicLinkAction),
      switchMap((action) => {
        return this.authService.signInWithMagicLink(action.input).pipe(
          map((res) => {
            if (res.signInWithMagicLink.token) {
              // Parse URL to separate path and query params
              const urlTree = this.router.parseUrl(action.url);
              this.router.navigateByUrl(urlTree);
              return signInActionSuccess(res.signInWithMagicLink);
            }
            throw Error('The magic link is incorrect');
          }),
          catchError(() => {
            if (action.errType === 'embed') {
              // For embed routes, show error inline instead of redirecting
              return [
                signInWithMagicLinkActionFailure({
                  showError: true,
                  errType: action.errType,
                }),
              ];
            } else {
              // For regular routes, show toastr and redirect to home
              this.toastr.error('Unable to sign in with magic link.');
              this.router.navigate(['/']);
              return [signInWithMagicLinkActionFailure({})];
            }
          })
        );
      })
    )
  );

  signInWithEmailMagicLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signInWithEmailMagicLinkAction),
      switchMap((action) =>
        this.authService.signInWithEmailMagicLink(action.input).pipe(
          map((res) => {
            if (res.signInWithEmailMagicLink.token) {
              this.router.navigate([action.url]);
              return signInActionSuccess(res.signInWithEmailMagicLink);
            }
            throw Error('The magic link is incorrect');
          }),
          catchError(() => {
            this.toastr.error('Unable to sign in with email magic link.');
            this.router.navigate(['/']);
            return [signInWithEmailMagicLinkActionFailure({ errors: [] })];
          })
        )
      )
    )
  );

  signInWithSmsMagicLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signInWithSmsMagicLinkAction),
      switchMap((action) =>
        this.authService.signInWithSmsMagicLink(action.input).pipe(
          map((res) => {
            if (res.signInWithSmsMagicLink.token) {
              this.router.navigate([action.url]);
              return signInActionSuccess(res.signInWithSmsMagicLink);
            }
            throw Error('The magic link is incorrect');
          }),
          catchError(() => {
            this.toastr.error('Unable to sign in with SMS magic link.');
            this.router.navigate(['/']);
            return [signInWithSmsMagicLinkActionFailure({ errors: [] })];
          })
        )
      )
    )
  );

  signUpUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(signUpAction),
      switchMap((action) =>
        this.authService.signUpUser(action).pipe(
          map((res) => {
            if (res.registerWithPassword.metadata?.token) {
              return signUpActionSuccess(res.registerWithPassword);
            } else {
              throw res.registerWithPassword.errors;
            }
          }),
          catchError((error) => {
            this.toastr.error(
              'Errors occurred during registration.',
              'Sign Up'
            );
            if (validateGraphQLErrors(error)) {
              return [signUpActionFailure({ errors: error })];
            }
            return [signUpActionFailure({ errors: [] })];
          })
        )
      )
    )
  );

  signUpUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signUpActionSuccess),
        tap(() => {
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'registrationComplete' });
          }

          this.store.dispatch(
            fetchFeatureFlagsAction({ keys: [...DEFAULT_FEATURE_FLAGS] })
          );

          this.toastr.clear();
          this.toastr.success('You have registered.');
          this.store.dispatch(
            fetchFeatureFlagsAction({ keys: [...DEFAULT_FEATURE_FLAGS] })
          );
          this.store.dispatch(setReferrerCodeAction({ referrerCode: null }));
          this.router.navigate(['/', 'home']);
        })
      ),
    { dispatch: false }
  );

  registerPasswordless$ = createEffect(() =>
    this.actions$.pipe(
      ofType(registerPasswordlessAction),
      switchMap((action) =>
        this.authService.registerPasswordless(action).pipe(
          map((res) => {
            if (res.registerPasswordless.metadata?.token) {
              return signUpActionSuccess(res.registerPasswordless);
            } else {
              throw res.registerPasswordless.errors;
            }
          }),
          catchError((error) => {
            this.toastr.error(
              'Errors occurred during registration.',
              'Sign Up'
            );
            if (validateGraphQLErrors(error)) {
              return [registerPasswordlessActionFailure({ errors: error })];
            }
            return [registerPasswordlessActionFailure({ errors: [] })];
          })
        )
      )
    )
  );

  signOutUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signOutAction),
        tap((action) => {
          this.store.dispatch(resetConfigAction());
          this.store.dispatch(resetPropertiesAction());
          this.store.dispatch(resetOffersAction());
          // this.store.dispatch(resetFiltersAction());
          this.store.dispatch(resetFavoritesAction());
          this.store.dispatch(resetBuyBoxesAction());
          this.store.dispatch(resetMyDealsAction());
          this.store.dispatch(resetMyOrdersAction());
          this.store.dispatch(resetMessagesAction());
          this.store.dispatch(
            fetchFeatureFlagsAction({ keys: [...DEFAULT_FEATURE_FLAGS] })
          );
          this.store.dispatch(setOrganizationUrlSlugAction({ slug: null }));
          if (action.skipRedirect) {
            return;
          }
          this.router.navigate(['/lp']);
        })
      ),
    { dispatch: false }
  );

  setupAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setupAccountAction),
      switchMap((action) => {
        return this.authService.createUserPreference(action).pipe(
          map((res) => {
            if (res.createUserPreference.result) {
              this.store.dispatch(setEntityIdAction({ entityId: null }));
              return setupAccountActionSuccess({
                preference: res.createUserPreference.result,
                cbsaPreferences: action.input.cbsaPreferences,
                entities: action.input.entities,
              });
            } else {
              throw res.createUserPreference.errors;
            }
          }),
          catchError((error) => {
            this.toastr.error('Errors occurred during account setup.');
            return [setupAccountActionFailure({ errors: error })];
          })
        );
      })
    )
  );

  setupAccountSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setupAccountActionSuccess),
        tap((resp) => {
          this.store.dispatch(setAuthModal({ authModal: 'welcome-user' }));
          if (
            resp.preference.roles &&
            resp.preference.roles?.includes('WHOLESALER')
          ) {
            this.router.navigate(['/my-organization']);
          }
        })
      ),
    { dispatch: false }
  );

  updateUserPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserPreferenceAction),
      switchMap((action) =>
        this.authService.updateUserPreference(action.id, action.input).pipe(
          map((res) => {
            if (res.updateUserPreference.result) {
              this.store.dispatch(fetchUserAction());
              return updateUserPreferenceActionSuccess({
                preference: res.updateUserPreference.result,
                cbsaPreferences: action.input.cbsaPreferences,
              });
            } else {
              throw res.updateUserPreference.errors;
            }
          }),
          catchError((error) => {
            this.toastr.error(
              'Errors occurred during updating user preference.'
            );
            return [updateUserPreferenceActionFailure({ errors: error })];
          })
        )
      )
    )
  );

  updateUserPreferenceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserPreferenceActionSuccess),
        tap(() => {
          this.toastr.clear();
          this.toastr.success('User preference has been updated.');
        })
      ),
    { dispatch: false }
  );

  fetchUserAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchUserAction),
      switchMap(() =>
        this.authService.fetchUser().pipe(
          map((res) => {
            if (res.self) {
              return fetchUserActionSuccess(res.self);
            } else {
              throw Error('Errors occurred during fetching user');
            }
          }),
          catchError(() => {
            return [fetchUserActionFailure({ errors: [] })];
          })
        )
      )
    )
  );

  requestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestPasswordResetAction),
      switchMap((action) =>
        this.authService.requestPasswordReset(action.email).pipe(
          map((res) => {
            if (res.requestPasswordReset && res.requestPasswordReset === 'OK') {
              this.toastr.success('Password reset link has been sent.');
              return requestPasswordResetActionFinish({ success: true });
            } else {
              throw Error('Errors occurred during password reset');
            }
          }),
          catchError((error) => {
            this.toastr.error('Errors occurred during password reset.');
            return [requestPasswordResetActionFinish({ success: false })];
          })
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPasswordAction),
      switchMap((action) =>
        this.authService.passwordResetWithPassword(action).pipe(
          map((res) => {
            if (res.passwordResetWithPassword.metadata.token) {
              this.toastr.success('Password has been reset.');
              this.router.navigate(['/']);
              setTimeout(() => {
                this.store.dispatch(setAuthModal({ authModal: 'signin' }));
              }, 0);
              return resetPasswordActionFinish();
            } else {
              throw Error('Errors occurred during password reset');
            }
          }),
          catchError(() => {
            this.toastr.error('Errors occurred during password reset.');
            return [resetPasswordActionFinish()];
          })
        )
      )
    )
  );

  sendPhoneNumberConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendPhoneNumberConfirmationAction),
      switchMap(() => {
        let lastSMSSentStore: string | undefined;
        const subscription = this.store
          .select(selectLastSMSSent)
          .subscribe((lastSMSSent) => {
            lastSMSSentStore = lastSMSSent;
          });

        if (lastSMSSentStore) {
          const lastSMSSent = new Date(lastSMSSentStore);
          const now = new Date();
          const diff = Math.abs(now.getTime() - lastSMSSent.getTime());
          const diffSeconds = Math.ceil(diff / 1000);
          if (diffSeconds < 30) {
            this.toastr.clear();
            this.toastr.error(
              'You can ask for confirmation code only once in 30 seconds.' +
                ' Please try again in ' +
                (30 - diffSeconds) +
                ' seconds.'
            );
            return [setLoadingAction({ loading: false })];
          }
        }

        this.store.dispatch(setLoadingAction({ loading: true }));
        this.store.dispatch(
          setLastSMSSentAction({ lastSMSSent: new Date().toString() })
        );

        return this.authService.sendPhoneNumberConfirmation().pipe(
          map((res) => {
            const { sendPhoneNumberConfirmation } = res;
            if (sendPhoneNumberConfirmation === 'OK') {
              this.toastr.success('Confirmation code has been sent.');
              subscription.unsubscribe();
              return setLoadingAction({ loading: false });
            } else {
              throw Error;
            }
          }),
          catchError(() => {
            this.toastr.error(
              'Errors occurred during confirmation code sending.'
            );
            return [setLoadingAction({ loading: false })];
          })
        );
      })
    )
  );

  confirmPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmPhoneNumberAction),
      switchMap((action) =>
        this.authService.confirmPhoneNumber(action).pipe(
          map((res) => {
            const { confirmPhoneNumber } = res;
            if (
              confirmPhoneNumber.result &&
              !confirmPhoneNumber.errors?.length
            ) {
              // GA event
              if (window.dataLayer) {
                window.dataLayer.push({ event: 'confirmationComplete' });
              }
              this.toastr.success('Your phone number has been confirmed.');
              this.store.dispatch(setAuthModal({ authModal: undefined }));
              return fetchUserActionSuccess(confirmPhoneNumber.result);
            } else {
              throw Error;
            }
          }),
          catchError(() => {
            this.toastr.error(
              'We could not confirm your phone number. ' +
                'Make sure you entered the correct code.'
            );
            return [setLoadingAction({ loading: false })];
          })
        )
      )
    )
  );

  updatePhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePhoneNumberAction),
      switchMap((action) =>
        this.authService.updatePhoneNumber(action).pipe(
          map((res) => {
            const { updatePhoneNumber } = res;
            if (updatePhoneNumber.result && !updatePhoneNumber.errors?.length) {
              this.toastr.success('Your phone number has been updated.');
              return updatePhoneNumberSuccessAction({
                success: true,
                user: updatePhoneNumber.result,
              });
            } else {
              throw updatePhoneNumber.errors;
            }
          }),
          catchError((err) => {
            this.toastr.error('Errors occurred during phone number update.');
            return [
              setLoadingAction({ loading: false }),
              updatePhoneNumberActionFailure({ errors: err }),
            ];
          })
        )
      )
    )
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProfileAction),
      switchMap((action) =>
        this.profileService.updateProfile(action).pipe(
          map((res) => {
            const { updateProfile } = res;
            if (updateProfile.result && !updateProfile.errors?.length) {
              this.toastr.success('Your profile has been updated.');
              return updateProfileActionSuccess({ ...res });
            } else {
              throw updateProfile.errors;
            }
          }),
          catchError((err) => {
            console.log(err);
            this.toastr.error('Errors occurred during profile update.');
            return [updateProfileActionFailure(err)];
          })
        )
      )
    )
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePasswordAction),
      switchMap((action) =>
        this.profileService.updatePassword(action).pipe(
          map((res) => {
            const { updatePassword } = res;
            if (updatePassword.result && !updatePassword?.errors.length) {
              this.toastr.success('Your password has been updated.');
              return updatePasswordActionSuccess();
            } else {
              throw updatePassword.errors;
            }
          }),
          catchError((err) => {
            console.log(err);
            this.toastr.error(
              'We could not update your password, ' +
                'please make sure you entered the correct current password.'
            );
            return [updatePasswordActionFailure(err)];
          })
        )
      )
    )
  );
}
